import React from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Select } from '@material-ui/core';
import { reducer } from './reducer';
import { BeaconInput } from './BeaconInput';

const isVideoBeacon = ({ event }) =>
  event !== 'click' && event !== 'impression';

const isNotVideoBeacon = beacon => !isVideoBeacon(beacon);

const VIDEO_BEACON_DISPLAY_NAMES = {
  'video-win-notification': 'Start',
  ten_second_silent_play: '10s',
  first_quartile: '25%',
  midpoint: '50%',
  third_quartile: '75%',
  completed_silent_play: 'Complete'
};
const VIDEO_BEACON_TYPES = Object.keys(VIDEO_BEACON_DISPLAY_NAMES);

const defaultVideoBeacons = creative => {
  const videoBeacons = ((creative && creative.beacons) || []).filter(
    isVideoBeacon
  );

  return videoBeacons.length === 0
    ? [{ event: VIDEO_BEACON_TYPES[0], url: '' }]
    : videoBeacons;
};

export const VideoBeaconList = ({
  creative,
  CreativeService,
  beaconRequired
}) => {
  const [beacons, dispatch] = React.useReducer(
    reducer,
    defaultVideoBeacons(creative)
  );

  React.useEffect(
    function overwriteVideoBeacons() {
      const previousBeacons = creative.beacons || [];
      CreativeService.set('beacons', [
        ...previousBeacons.filter(isNotVideoBeacon),
        ...beacons
      ]);
    },
    [beacons]
  );

  React.useEffect(
    function updateRequiredFlags() {
      dispatch({ type: 'setRequired', payload: { required: beaconRequired } });
    },
    [beaconRequired]
  );

  const handleVendorChange = (index, vendorId) => {
    dispatch({ type: 'assignVendor', index, vendorId });
  };

  return (
    <>
      {beacons.map((beacon, index) => {
        const setBeaconType = type => {
          dispatch({
            type: 'updateBeacon',
            index,
            payload: { event: type }
          });
        };

        return (
          <div key={index}>
            <div className="col-xs-3 mt1">
              <Select
                fullWidth
                value={beacon.event}
                onChange={event => setBeaconType(event.target.value)}
              >
                {VIDEO_BEACON_TYPES.map(beaconType => (
                  <MenuItem key={beaconType} value={beaconType}>
                    {VIDEO_BEACON_DISPLAY_NAMES[beaconType]}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="col-xs-9 pr0">
              <BeaconInput
                id={`video_beacon_${index}`}
                applyGdprValidations={!!creative.runningInEu}
                nameAttr={`video_beacon_${index}`}
                labelContent="Video Event Tracker"
                value={beacon.url}
                onInputChange={event =>
                  dispatch({
                    type: 'updateBeacon',
                    index,
                    payload: { url: event.target.value }
                  })
                }
                onVendorChange={vendorId => handleVendorChange(index, vendorId)}
                button={
                  index === beacons.length - 1 ? (
                    <button
                      className="btn btn-default"
                      type="button"
                      onClick={() =>
                        dispatch({ type: 'addBeacon', event: beacon.event })
                      }
                    >
                      + New Video Tag
                    </button>
                  ) : (
                    <button
                      className="btn btn-default"
                      type="button"
                      onClick={() => dispatch({ type: 'removeBeacon', index })}
                    >
                      Remove
                    </button>
                  )
                }
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

VideoBeaconList.propTypes = {
  creative: PropTypes.shape({
    runningInEu: PropTypes.bool,
    action: PropTypes.string,
    beacons: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        event: PropTypes.string
      })
    )
  }).isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func.isRequired
  }).isRequired,
  beaconRequired: PropTypes.bool.isRequired
};
